import React, { use } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import styles from '@pages-clean/admin/study/admin-data-views/data-views/details/style.module.scss';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { CTQFactorsContext } from '@pages-clean/risk-assessment/interfaces/context';
import { ShowError, ShowSuccess } from '@utils/notification';
import { FunctionalDataReviewsIncludingCtQfDetailFields } from '@queries/generated/fragments.generated';
import {
    deleteFunctionalDataReview,
    deleteFunctionalDataReviewVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    CTQ_WIDGET_VALUES,
    DELETE_FUNCTIONAL_DATA_REVIEW,
    GET_CRITICAL_VARIABLES,
    GET_FUNCTIONAL_DATA_REVIEWS_WITH_CTQF_DETAIL,
} from '@queries/risk-assessment';

interface DeleteFunctionalDataReviewProps {
    onClose: () => void;
    fdr: FunctionalDataReviewsIncludingCtQfDetailFields;
}

export type DeleteFdrFormInputs = {
    reason: string;
};

const DeleteFunctionalDataReviewDialog = ({ onClose, fdr }: DeleteFunctionalDataReviewProps) => {
    const { t } = useTranslation('ram');
    const { t: tCommon } = useTranslation('common');
    const { current } = use(CTQFactorsContext);
    const {
        control,
        getValues,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<DeleteFdrFormInputs>({
        mode: 'all',
        defaultValues: { reason: '' },
    });

    const [deleteFdr] = useMutation<deleteFunctionalDataReview, deleteFunctionalDataReviewVariables>(
        DELETE_FUNCTIONAL_DATA_REVIEW,
    );

    const handleSubmit = () => {
        const formValues = getValues();
        deleteFdr({
            variables: { id: fdr.id, reason: formValues.reason },
            refetchQueries: [
                { query: GET_FUNCTIONAL_DATA_REVIEWS_WITH_CTQF_DETAIL, variables: { studyId: current.study?.id } },
                { query: GET_CRITICAL_VARIABLES, variables: { studyId: current.study?.id } },
                { query: CTQ_WIDGET_VALUES, variables: { studyId: current.study?.id } },
            ],
        })
            .then((response) => {
                if (response?.data?.deleteFunctionalDataReview) {
                    ShowSuccess(tCommon('actionsSuccessfulToast'));
                    onClose();
                } else {
                    ShowError(tCommon('errorMessage'));
                }
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const warningText = (
        <span className={cn('k-text-left', style.fontSize)}>
            {t('ctqf.functionalDataReviews.grid.warnings.deleteFdr')}
            <span className={styles.textHighLight}>
                {fdr.ctQfDescription ?? current.selectionState.selectedItem?.description}
            </span>
        </span>
    );

    return (
        <ConfirmationDialog
            title={`${t('deleteFunctionalDataReview.title')}: ${fdr.recordId}`}
            dataTestId="confirmation-dialog"
            onClose={() => onClose()}
            onSubmit={() => handleSubmit()}
            className="info"
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            primaryBtnLabel={tCommon('delete')}
            width={522}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                <InfoField type="Warning" message={warningText} />
                <TextAreaController
                    className={style.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="reason"
                    required
                    data-testid="fdr-delete-reason-form"
                    label={t('deleteFunctionalDataReview.reason')}
                    placeholder=""
                />
            </div>
        </ConfirmationDialog>
    );
};

export default DeleteFunctionalDataReviewDialog;
